import dynamic from "next/dynamic";
import Image from "next/image";
import React, { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import type { Swiper as SwiperCore } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { SwiperSlide } from "swiper/react";
import { Swiper } from "swiper/react";
import { PinPointKing } from "~/components/Map/PinPoint";
import { CenteredPulseLoader } from "~/guidelines/Loaders";

const MapboxWebGLDynamic = dynamic(
  () => import("~/components/Map/MapboxWebGL"),
  {
    ssr: false,
    loading: () => <CenteredPulseLoader />,
  },
);

interface GalleryItemCaption {
  _type?: string;
  en: string;
  fr: string;
}

interface GalleryAsset {
  url: string | null;
}

interface GalleryItem {
  asset: GalleryAsset;
  caption: GalleryItemCaption | null;
}

interface MapMarker {
  label: string;
  longitude: number;
  latitude: number;
  kind: PinPointKing;
  zoom: number;
}

interface MapData {
  latitude: number;
  longitude: number;
  zoom: number;
  marker: MapMarker;
}

interface MediaCarouselProps {
  gallery: GalleryItem[];
  mapData: MapData;
  className?: string;
  maxWidth?: string;
  height?: string;
  mobileHeight?: string;
  borderRadius?: string;
  customBorderRadius?: {
    desktop?: {
      topLeft?: string;
      topRight?: string;
      bottomLeft?: string;
      bottomRight?: string;
    };
    mobile?: {
      topLeft?: string;
      topRight?: string;
      bottomLeft?: string;
      bottomRight?: string;
    };
  };
}

const MediaContainer = styled.div<{
  maxWidth?: string;
  height?: string;
  mobileHeight?: string;
  borderRadius?: string;
  customBorderRadius?: {
    desktop?: {
      topLeft?: string;
      topRight?: string;
      bottomLeft?: string;
      bottomRight?: string;
    };
    mobile?: {
      topLeft?: string;
      topRight?: string;
      bottomLeft?: string;
      bottomRight?: string;
    };
  };
}>`
  position: relative;
  max-width: ${({ maxWidth }) => maxWidth || "100vw"};
  width: 100%;
  height: ${({ height }) => height || "500px"};
  margin-left: auto;
  margin-right: auto;
  border-radius: ${({ borderRadius, customBorderRadius, theme }) => {
    if (customBorderRadius?.desktop) {
      const { topLeft, topRight, bottomLeft, bottomRight } =
        customBorderRadius.desktop;
      return `${topLeft || theme.borderRadius?.large || "8px"} ${topRight || "0"} ${bottomRight || "0"} ${bottomLeft || theme.borderRadius?.large || "8px"}`;
    }
    return borderRadius || theme.borderRadius?.large || "8px";
  }};
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    max-width: 100vw;
    height: ${({ mobileHeight }) => mobileHeight || "200px"};
    border-radius: ${({ borderRadius, customBorderRadius, theme }) => {
      if (customBorderRadius?.mobile) {
        const { topLeft, topRight, bottomLeft, bottomRight } =
          customBorderRadius.mobile;
        return `${topLeft || theme.borderRadius?.large || "8px"} ${topRight || theme.borderRadius?.large || "8px"} ${bottomRight || "0"} ${bottomLeft || "0"}`;
      }
      return borderRadius || theme.borderRadius?.large || "8px";
    }};
  }
`;

const MapViewContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 200px;
`;

const StyledSwiper = styled((props) => <Swiper {...props} />)`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .swiper-slide {
    position: relative;
    width: 100% !important;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;

    img {
      max-width: 100% !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    color: black;
    background-color: white;
    border-radius: 100%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;

    &:hover {
      opacity: 0.8;
    }

    &::after {
      font-size: 8px;
      font-weight: bold;
    }
  }

  .swiper-button-prev {
    left: 8px;
  }

  .swiper-button-next {
    right: 8px;
  }

  .swiper-pagination-bullets {
    bottom: 16px;
  }

  .swiper-pagination-bullet {
    background-color: ${({ theme }) => theme.color.background.lightFirst};
    opacity: 0.5;
    width: 6px;
    height: 6px;
  }

  .swiper-pagination-bullet-active {
    opacity: 1;
    background-color: ${({ theme }) => theme.color.text.secondaryStrong};
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
  border: 3px solid black;
  border-radius: 8px;
  cursor: pointer;
  width: 160px;
  height: 100px;
  overflow: hidden;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 70px;
    height: 60px;
  }
`;

const MediaCarousel: FC<MediaCarouselProps> = ({
  gallery,
  mapData,
  className,
  maxWidth,
  height,
  mobileHeight,
  borderRadius,
  customBorderRadius,
}) => {
  const [showMap, setShowMap] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const swiperRef = useRef<SwiperCore | null>(null);

  const toggleView = () => setShowMap(!showMap);

  useEffect(() => {
    const handleResize = () => {
      if (swiperRef.current) {
        swiperRef.current.update();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <MediaContainer
      className={className}
      maxWidth={maxWidth}
      height={height}
      mobileHeight={mobileHeight}
      borderRadius={borderRadius}
      customBorderRadius={customBorderRadius}
    >
      {showMap ? (
        <MapViewContainer>
          <MapboxWebGLDynamic
            latitude={mapData.latitude}
            longitude={mapData.longitude}
            zoom={mapData.zoom}
            markers={[
              {
                ...mapData.marker,
                label: mapData.marker.label ?? "",
              },
            ]}
          />

          <ToggleButton onClick={toggleView}>
            <Image
              src={gallery?.[currentSlideIndex]?.asset.url ?? ""}
              alt="Show carousel"
              fill
              style={{ objectFit: "cover" }}
            />
          </ToggleButton>
        </MapViewContainer>
      ) : (
        <StyledSwiper
          modules={[Navigation, Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          onSlideChange={(swiper: SwiperCore) =>
            setCurrentSlideIndex(swiper.realIndex)
          }
          onSwiper={(swiper: SwiperCore) => (swiperRef.current = swiper)}
        >
          {gallery?.map((src, index) => (
            <SwiperSlide key={index}>
              <Image
                src={src.asset.url ?? ""}
                alt={`Slide ${index + 1}`}
                fill
                style={{ objectFit: "cover" }}
              />
            </SwiperSlide>
          ))}

          <ToggleButton onClick={toggleView}>
            <div
              style={{
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <MapboxWebGLDynamic
                latitude={mapData.marker.latitude}
                longitude={mapData.marker.longitude}
                zoom={mapData.marker.zoom}
                markers={[
                  {
                    ...mapData.marker,
                    label: mapData.marker.label ?? "",
                  },
                ]}
                showZoomControls={false}
                showAttribution={false}
                miniMapMode={true}
              />
            </div>
          </ToggleButton>
        </StyledSwiper>
      )}
    </MediaContainer>
  );
};

export default MediaCarousel;
